/**
 * Mapping from bulk_document_review_job job items to the outcome shape
 * the admin review-summary UI consumes. The job stores its per-item
 * result as the summary artifact; this mapper is the one place that
 * translates it back into display outcomes, so the summary the admin
 * re-opens after a background run is exactly what the job recorded —
 * including which approvals the job already applied.
 */

export type BulkReviewAppliedState = 'auto_applied' | 'pending' | 'skipped_decided';

export interface BulkReviewJobItemOutcome {
  docId: string;
  docName: string;
  decision: 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW';
  reason: string;
  identityStatus?: string;
  aiReview?: any;
  error?: string;
  engine?: string;
  appliedState: BulkReviewAppliedState;
  appliedAt?: string;
  note?: string;
}

const DECISIONS = new Set(['APPROVED', 'REJECTED', 'NEEDS_REVIEW']);
const APPLIED_STATES = new Set(['auto_applied', 'pending', 'skipped_decided']);

export function mapBulkReviewJobItem(item: {
  status?: string;
  entityId?: string | null;
  docName?: string | null;
  result?: Record<string, any> | null;
  error?: { message?: string } | null;
}): BulkReviewJobItemOutcome | null {
  const result = item?.result || null;
  if (!result || typeof result !== 'object' || !result.docId) {
    // Item never produced an outcome (job failed it, or it has not
    // run yet): surface it honestly as needing a human, never as a
    // verdict. The document identity comes from the job item itself
    // when the result never landed.
    const docId = String((result as any)?.docId || item?.entityId || '');
    if (!docId) return null;
    return {
      docId,
      docName: String((result as any)?.docName || item?.docName || docId),
      decision: 'NEEDS_REVIEW',
      reason: 'The review could not be completed for this document.',
      error: item?.error?.message || 'Review did not complete.',
      appliedState: 'pending',
    };
  }

  const decision = DECISIONS.has(String(result.decision)) ? result.decision : 'NEEDS_REVIEW';
  const appliedState = APPLIED_STATES.has(String(result.appliedState))
    ? (result.appliedState as BulkReviewAppliedState)
    : 'pending';

  return {
    docId: String(result.docId),
    docName: String(result.docName || result.docId),
    decision,
    reason: String(result.reason || ''),
    identityStatus: result.identityStatus ? String(result.identityStatus) : undefined,
    aiReview: result.aiReview || null,
    error: result.error ? String(result.error) : undefined,
    engine: result.engine ? String(result.engine) : undefined,
    appliedState,
    appliedAt: result.appliedAt ? String(result.appliedAt) : undefined,
    note: result.note ? String(result.note) : undefined,
  };
}
